<template>
  <div class="groups">
    <v-dialog v-model="showDialog" max-width="400">
      <v-card class="pa-4">
        <v-card-title class="headline">New Group</v-card-title>

        <v-card-text>
          <v-text-field
            v-model="newGroupKey"
            label="Group ID"
            hint="The group ID must be unique (otherwise it will overwrite the current group using this ID). The ID must not contain '/', '.', '#', '$', '[', or ']'"
            persistent-hint
            outlined
          ></v-text-field>
          <v-text-field
            v-model="newGroupName"
            label="Name"
            outlined
          ></v-text-field>
        </v-card-text>

        <v-card-actions>
          <v-spacer></v-spacer>
          <v-btn text @click="showDialog = false">
            Cancel
          </v-btn>
          <v-btn color="primary" @click="createGroup">
            Add
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <v-container>
      <v-row>
        <template v-for="(value, key, index) in dbGroups">
          <v-col :key="key" cols="6">
            <v-card outlined>
              <v-list-item three-line>
                <v-list-item-content>
                  <div class="overline mb-4">
                    <v-badge
                      :content="getGroupStudies(key).length"
                      :value="getGroupStudies(key).length > 0"
                      color="primary"
                      inline
                      bordered
                    >
                      {{ key }}
                    </v-badge>
                  </div>
                  <v-list-item-title class="headline mb-1">
                    {{ value.name }}
                  </v-list-item-title>
                  <v-list-item-subtitle
                    v-if="dbAdminUsers[dbGroups[key].assignedRN]"
                    >RN:
                    {{
                      dbAdminUsers[dbGroups[key].assignedRN].name
                    }}</v-list-item-subtitle
                  >
                  <v-list-item-subtitle
                    v-if="dbAdminUsers[dbGroups[key].assignedMD]"
                    >MD:
                    {{
                      dbAdminUsers[dbGroups[key].assignedMD].name
                    }}</v-list-item-subtitle
                  >
                </v-list-item-content>
              </v-list-item>

              <v-divider />
              <v-card-actions>
                <v-spacer />
                <v-btn text color="primary" @click="viewGroup(key, value)">
                  Edit
                </v-btn>
                <v-btn
                  icon
                  @click="toggleGroupDetails(index)"
                  :disabled="getGroupStudies(key).length <= 0"
                >
                  <v-icon>{{
                    groupsDetails[index] ? "mdi-chevron-up" : "mdi-chevron-down"
                  }}</v-icon>
                </v-btn>
              </v-card-actions>

              <v-expand-transition>
                <div v-show="groupsDetails[index]">
                  <v-divider></v-divider>

                  <v-card-text>
                    <v-chip
                      class="ma-1"
                      close
                      color="primary"
                      text-color="white"
                      close-icon="mdi-close"
                      @click:close="updateGroup(study.id, null, null)"
                      v-for="study in getGroupStudies(key)"
                      :key="study.id"
                    >
                      {{ study.id }}
                    </v-chip>
                  </v-card-text>
                </div>
              </v-expand-transition>
            </v-card>
          </v-col>
        </template>
      </v-row>
      <v-divider class="mt-4 mb-6" />
      <v-row>
        <v-btn
          class="mx-auto mb-4"
          color="primary"
          @click="showDialog = true"
          v-if="userRole === 'admin' || userRole === 'manager'"
          >Create Group<v-icon right>mdi-plus</v-icon></v-btn
        >
      </v-row>
      <v-row>
        <v-card class="mx-auto">
          <div class="overline my-3 mx-4">UNASSIGNED</div>
          <v-divider />

          <v-list two-line>
            <template v-for="(study, index) in getGroupStudies()">
              <v-list-item :key="study.id">
                <v-list-item-content>
                  <v-list-item-title v-text="study.id"></v-list-item-title>
                  <v-list-item-subtitle v-if="study.params"
                    >Counting carbs:
                    <b>{{
                      study.params.isAM ? "ON" : "OFF"
                    }}</b></v-list-item-subtitle
                  >
                </v-list-item-content>

                <v-list-item-action>
                  <v-menu open-on-hover top offset-y>
                    <template v-slot:activator="{ on }">
                      <v-btn text icon color="primary" v-on="on">
                        <v-icon>mdi-plus</v-icon>
                      </v-btn>
                    </template>

                    <v-list>
                      <v-list-item
                        v-for="(value, key) in dbGroups"
                        :key="study.id + key"
                        @click="updateGroup(study.id, key, value)"
                      >
                        <v-list-item-title>{{ value.name }}</v-list-item-title>
                      </v-list-item>
                    </v-list>
                  </v-menu>
                </v-list-item-action>
              </v-list-item>

              <v-divider
                v-if="index + 1 < getGroupStudies().length"
                :key="index"
              ></v-divider>
            </template>
          </v-list>
        </v-card>
      </v-row>
    </v-container>

    <v-dialog v-model="viewGroupDialog" scrollable max-width="500px">
      <v-card v-if="currentGroupDialog">
        <v-form ref="viewGroupForm" v-model="groupValid" lazy-validation>
          <v-card-title>Group</v-card-title>
          <v-divider></v-divider>
          <v-card-text style="height: 300px; overflow-y: auto">
            <div class="overline mb-4">General Settings</div>

            <v-text-field
              v-model="currentGroupDialog.name"
              :rules="[$rules.required]"
              label="Group Name"
              required
              outlined
              hide-details="auto"
              class="mb-2"
            ></v-text-field>

            <v-select
              :items="surveysArray"
              v-model="currentGroupDialog.surveyId"
              label="Survey"
              outlined
              hide-details="auto"
              class="mb-2"
            ></v-select>

            <v-select
              :items="adminUsersArray"
              v-model="currentGroupDialog.assignedRN"
              label="Assigned RN"
              outlined
              hide-details="auto"
              class="mb-2"
            ></v-select>

            <v-select
              :items="adminUsersArray"
              v-model="currentGroupDialog.assignedMD"
              label="Assigned MD"
              outlined
              hide-details="auto"
              class="mb-2"
            ></v-select>

            <v-divider class="mt-4" />

            <div class="overline my-4">Send Mood Feedback</div>
            <div class="d-flex align-center">
              <v-switch
                v-model="currentGroupDialog.sendMoodFeedbackAuto"
                label="Send Automatically"
              ></v-switch>
              <v-spacer />
              <v-btn
                class="mb-1"
                @click="groupSendMoodFeedback(currentGroupDialog.id)"
              >
                Send Manually
              </v-btn>
            </div>
            <div class="d-flex align-center">
              On
              <v-select
                v-model="currentGroupDialog.sendMoodFeedbackDay"
                :items="daysArray"
                label="Day"
                solo
                hide-details
                class="mx-2"
                :disabled="!currentGroupDialog.sendMoodFeedbackAuto"
              ></v-select>
              every
              <v-select
                v-model="currentGroupDialog.sendMoodFeedbackFrequency"
                :items="weeksArray"
                label="Frequency"
                solo
                hide-details
                class="mx-2"
                :disabled="!currentGroupDialog.sendMoodFeedbackAuto"
              ></v-select>
              weeks
            </div>
            <div class="d-flex align-center mt-4">
              Start on
              <v-menu
                v-model="datePickerMoodFeedback"
                :close-on-content-click="false"
                :nudge-right="40"
                transition="scale-transition"
                offset-y
                min-width="290px"
              >
                <template v-slot:activator="{ on }">
                  <v-text-field
                    v-model="currentGroupDialog.sendMoodFeedbackStart"
                    prepend-inner-icon="event"
                    readonly
                    solo
                    v-on="on"
                    hide-details
                    class="ml-2"
                    :disabled="
                      !currentGroupDialog.sendMoodFeedbackAuto ||
                        !currentGroupDialog.sendMoodFeedbackDay
                    "
                  ></v-text-field>
                </template>
                <v-date-picker
                  v-model="currentGroupDialog.sendMoodFeedbackStart"
                  no-title
                  :allowed-dates="allowedDatesMoodFeedback"
                  @input="datePickerMoodFeedback = false"
                ></v-date-picker>
              </v-menu>
            </div>

            <v-divider class="mt-4" />

            <div class="overline my-4">Send Survey</div>
            <div class="d-flex align-center">
              <v-switch
                v-model="currentGroupDialog.sendSurveyAuto"
                label="Send Automatically"
              ></v-switch>
              <v-spacer />
              <v-btn
                class="mb-1"
                @click="groupFillSurvey(currentGroupDialog.id)"
              >
                Send Manually
              </v-btn>
            </div>
            <div class="d-flex align-center">
              On
              <v-select
                v-model="currentGroupDialog.sendSurveyDay"
                :items="daysArray"
                label="Day"
                solo
                hide-details
                class="mx-2"
                :disabled="!currentGroupDialog.sendSurveyAuto"
              ></v-select>
              every
              <v-select
                v-model="currentGroupDialog.sendSurveyFrequency"
                :items="weeksArray"
                label="Frequency"
                solo
                hide-details
                class="mx-2"
                :disabled="!currentGroupDialog.sendSurveyAuto"
              ></v-select>
              weeks
            </div>
            <div class="d-flex align-center my-4">
              Start on
              <v-menu
                v-model="datePickerSurvey"
                :close-on-content-click="false"
                :nudge-right="40"
                transition="scale-transition"
                offset-y
                min-width="290px"
              >
                <template v-slot:activator="{ on }">
                  <v-text-field
                    v-model="currentGroupDialog.sendSurveyStart"
                    prepend-inner-icon="event"
                    readonly
                    solo
                    v-on="on"
                    hide-details
                    class="ml-2"
                    :disabled="
                      !currentGroupDialog.sendSurveyAuto ||
                        !currentGroupDialog.sendSurveyDay
                    "
                  ></v-text-field>
                </template>
                <v-date-picker
                  v-model="currentGroupDialog.sendSurveyStart"
                  no-title
                  :allowed-dates="allowedDatesSurvey"
                  @input="datePickerSurvey = false"
                ></v-date-picker>
              </v-menu>
            </div>
          </v-card-text>
          <v-divider></v-divider>
          <v-card-actions>
            <v-spacer />
            <v-btn
              color="error"
              text
              @click="deleteGroup(currentGroupDialog.id)"
              :disabled="getGroupStudies(currentGroupDialog.id).length > 0"
              >Delete</v-btn
            >
            <v-btn
              color="primary"
              @click="saveGroup(currentGroupDialog.id, currentGroupDialog)"
              :disabled="!groupValid"
              >Save</v-btn
            >
          </v-card-actions>
        </v-form>
      </v-card>
    </v-dialog>
  </div>
</template>

<style scoped>
.md-card {
  display: inline-block;
  width: 400px;
  margin: 5px;
}
.add-button {
  float: right;
}

.group-chips {
  display: flex;
  width: 100%;
  flex-wrap: wrap;
  padding-left: 72px;
}
.group-chips .md-chip {
  margin: 2.5px;
}

.groups .md-field {
  margin: 5px 0;
}
</style>

<script>
import firebase from "firebase/app";
import { EventBus } from "../main";
import { ROLES, DAYS_ARRAY } from "../globals";
import moment from "moment";

import { mapState } from "vuex";

export default {
  name: "groups",
  data: () => ({
    showDialog: false,
    viewGroupDialog: false,
    currentGroupDialog: null,
    groupValid: false,
    surveys: null,
    newGroupKey: null,
    newGroupName: null,
    groupsDetails: [],
    daysArray: DAYS_ARRAY,
    weeksArray: [1, 2, 3, 4, 5, 6],
    datePickerMoodFeedback: false,
    datePickerSurvey: false
  }),
  computed: {
    ...mapState(["dbStudies", "dbGroups", "dbAdminUsers", "userRole"]),
    surveysArray: function() {
      let arr = [{ text: "None", value: null }];
      return this.surveys ? [...arr, ...Object.keys(this.surveys)] : arr;
    },
    adminUsersArray: function() {
      let arr = [{ text: "Unassigned", value: null }];
      for (let id in this.dbAdminUsers) {
        let obj = this.dbAdminUsers[id];
        if (!obj.deleted) {
          arr.push({ text: `${ROLES[obj.role]} - ${obj.name}`, value: id });
        }
      }
      return arr;
    }
  },
  mounted() {
    this.groupsDetails = Object.values(this.dbGroups).map(() => false);
  },
  methods: {
    getGroupStudies(groupId) {
      return groupId
        ? this.dbStudies.filter(study => study.group === groupId)
        : this.dbStudies.filter(study => !study.group);
    },
    updateGroup(studyId, groupId, group) {
      let surveyId = (group && group.surveyId) ? group.surveyId : null;
      this.$store.dispatch("updateGroup", { studyId, groupId, surveyId });
    },
    async createGroup() {
      await firebase
        .database()
        .ref("/Groups/")
        .child(this.newGroupKey)
        .set({ name: this.newGroupName }, error => {
          if (error) {
            EventBus.$emit("showSnackbar", "There was an error...");
            console.log(error);
          } else {
            EventBus.$emit("showSnackbar", "Saved successfully!");
          }
        });

      this.newGroupKey = null;
      this.newGroupName = null;
      this.showDialog = false;

      this.$store.dispatch("fetchGroups");
    },
    updateStudySurveyId(studyId, surveyId) {
      firebase
        .database()
        .ref("/Studies/")
        .child(studyId)
        .update({
          surveyId: surveyId
        });
    },
    updateStudySendMoodFeedback(studyId) {
      firebase
        .database()
        .ref("/Studies/")
        .child(studyId)
        .update({
          requiredToSendMoodFeedback: true
        });
    },
    updateStudyFillSurvey(studyId) {
      firebase
        .database()
        .ref("/Studies/")
        .child(studyId)
        .update({
          requiredToOpenSurvey: true
        });
    },
    saveGroup(groupId, group) {
      if (this.$refs.viewGroupForm.validate()) {
        this.viewGroupDialog = false;

        let surveyId = group.surveyId ? group.surveyId : null;

        this.getGroupStudies(groupId).forEach(study => {
          this.updateStudySurveyId(study.id, surveyId);
        });

        delete group.id;

        firebase
          .database()
          .ref("/Groups/")
          .child(groupId)
          .set(group, error => {
            if (error) {
              EventBus.$emit("showSnackbar", "There was an error...");
              console.log(error);
            } else {
              EventBus.$emit("showSnackbar", "Saved successfully!");
            }
          });
      }
    },
    async deleteGroup(groupId) {
      if (this.getGroupStudies(groupId).length > 0) {
        alert("Cannot delete a group associated with studies");
      } else {
        this.viewGroupDialog = false;

        await firebase
          .database()
          .ref("/Groups/")
          .child(groupId)
          .remove();

        this.$store.dispatch("fetchGroups");
      }
    },
    groupSendMoodFeedback(groupId) {
      this.getGroupStudies(groupId).forEach(study => {
        this.updateStudySendMoodFeedback(study.id);
      });
    },
    groupFillSurvey(groupId) {
      this.getGroupStudies(groupId).forEach(study => {
        this.updateStudyFillSurvey(study.id);
      });
    },
    viewGroup(groupId, groupObj) {
      groupObj.id = groupId;
      this.currentGroupDialog = groupObj;
      this.viewGroupDialog = true;
    },
    toggleGroupDetails(index) {
      let newVal = !this.groupsDetails[index];
      this.groupsDetails.splice(index, 1, newVal);
    },
    allowedDatesMoodFeedback(val) {
      if (this.currentGroupDialog.sendMoodFeedbackDay) {
        return (
          moment(val).isoWeekday() ===
          this.currentGroupDialog.sendMoodFeedbackDay
        );
      } else {
        return false;
      }
    },
    allowedDatesSurvey(val) {
      if (this.currentGroupDialog.sendSurveyDay) {
        return (
          moment(val).isoWeekday() === this.currentGroupDialog.sendSurveyDay
        );
      } else {
        return false;
      }
    }
  },
  created() {
    firebase
      .database()
      .ref("/Surveys/")
      .once("value", snapshot => (this.surveys = snapshot.val()));
  }
};
</script>
